import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TelemetryService } from './telemetry.service';

/**
 * Records every Material dialog open as a telemetry event, from one place. {@link MatDialog.afterOpened}
 * emits for every dialog opened anywhere in the app, so a single subscription covers all call sites without
 * touching them — and keeps working as new dialogs are added.
 */
@Injectable({ providedIn: 'root' })
export class DialogTelemetryService {
  private readonly dialog = inject(MatDialog);
  private readonly telemetry = inject(TelemetryService);

  /** Subscribes once to app-wide dialog opens. Registered as an app initializer. */
  init(): void {
    this.dialog.afterOpened.subscribe((ref: MatDialogRef<unknown>) => {
      this.telemetry.trackEvent('DialogOpened', {
        dialog: this.resolveName(ref),
        dialogId: ref.id,
      });
    });
  }

  // Identify the dialog by its component's element selector (e.g. "app-confirm-dialog"): the selector is a
  // template string baked into the DOM, so it survives production minification — unlike the component class
  // name, which the optimizer mangles to something like "t". Fall back to the class name if no host tag is
  // available (defensive; Material always renders the component into a host element).
  private resolveName(ref: MatDialogRef<unknown>): string {
    const tag = ref.componentRef?.location?.nativeElement?.tagName;
    if (typeof tag === 'string' && tag.length > 0) {
      return tag.toLowerCase();
    }
    return ref.componentInstance?.constructor?.name ?? 'unknown';
  }
}
